import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Spinner,
  Switch,
  Textarea,
} from '@agion/ui'
import { Database, Eye, History, RefreshCw, Server } from 'lucide-react'
import { ApiError } from '@/core/infra_api_client'
import type { RolloutControlProps } from './rollout_catalog'
import {
  excludeIngestionConnector,
  getIngestionOffering,
  includeIngestionConnector,
  listIngestionOfferingHistory,
  type IngestionOffering,
  type IngestionOfferingAction,
  type IngestionOfferingHistoryEntry,
  type IngestionOfferingHostStatus,
  type IngestionOfferingState,
} from './infra_ingestion_offering.service'

const historyDateOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }
const reasonMaxBytes = 500

interface PendingChange {
  connectorType: string
  action: IngestionOfferingAction
}

interface OfferingRow {
  connectorType: string
  displayName: string
  create: boolean
  sync: boolean
  count: number
  hostCount: number
  /** An exclusion no online host declares: it can only be lifted. */
  stale: boolean
}

// The record keeps the four statuses exhaustive against the type; the Map is
// what the render reads. A status arrives on the wire, and an object lookup
// resolves 'toString' or '__proto__' to a prototype member instead of missing,
// handing the translator something that is not a key.
const hostStatusKeyByStatus: Record<IngestionOfferingHostStatus, string> = {
  declared: 'rolloutControls.ingestionOffering.hostDeclared',
  unsupported: 'rolloutControls.ingestionOffering.hostUnsupported',
  unavailable: 'rolloutControls.ingestionOffering.hostUnavailable',
  unreadable: 'rolloutControls.ingestionOffering.hostUnreadable',
}
const hostStatusKeys = new Map<string, string>(Object.entries(hostStatusKeyByStatus))

/**
 * The reasons governance-service is allowed to report for an unavailable
 * host. Any other value is dropped rather than shown raw, because the
 * reason originates upstream of the control plane.
 */
const hostReasonKeys = new Map<string, string>([
  ['not_bound', 'rolloutControls.ingestionOffering.hostReasonNotBound'],
  ['unreachable', 'rolloutControls.ingestionOffering.hostReasonUnreachable'],
  ['timeout', 'rolloutControls.ingestionOffering.hostReasonTimeout'],
])

/**
 * Organisation-level control over the ingestion connectors offered in the
 * admin console (PLAN-20260911 slice 6). One row per connector the
 * organisation's online hosts declare through their ingestion workers, as
 * governance-service composed it; excluding a connector removes it from the
 * connector picker on the next page load. The card never derives a row from
 * the organisation's resource inventory: a resource type no worker declares
 * is listed separately as uncovered. An exclusion no host declares stays
 * visible so it can be lifted, and is never presented as offered. Superusers
 * edit, members read: the offering answer carries the server's editable
 * verdict, so the card never probes a route that would answer 403 (the
 * shared API client redirects the whole page on 403).
 */
export function IngestionOfferingCard({ organization }: RolloutControlProps) {
  const { t, i18n } = useTranslation('pages')
  const activeOrganizationId = useRef(organization.id)
  const loadGeneration = useRef(0)
  const mutationGeneration = useRef(0)
  activeOrganizationId.current = organization.id
  const [offering, setOffering] = useState<IngestionOffering | null>(null)
  const [history, setHistory] = useState<IngestionOfferingHistoryEntry[]>([])
  const [pending, setPending] = useState<PendingChange | null>(null)
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [historyWarning, setHistoryWarning] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const isCurrent = useCallback(
    (generation: number, ref: { current: number }, requestedOrganizationId: string) =>
      generation === ref.current && activeOrganizationId.current === requestedOrganizationId,
    [],
  )

  const refreshHistory = useCallback(async (generation: number, ref: { current: number }, requestedOrganizationId: string) => {
    try {
      const currentHistory = await listIngestionOfferingHistory()
      if (!isCurrent(generation, ref, requestedOrganizationId)) return
      setHistory(currentHistory)
    } catch {
      if (!isCurrent(generation, ref, requestedOrganizationId)) return
      setHistoryWarning(t('rolloutControls.ingestionOffering.historyRefreshError'))
    }
  }, [isCurrent, t])

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current
    const requestedOrganizationId = organization.id
    setIsLoading(true)
    setError(null)
    setHistoryWarning(null)
    setSaved(false)
    setOffering(null)
    setHistory([])
    setPending(null)
    setReason('')
    setConfirmed(false)
    try {
      const currentOffering = await getIngestionOffering()
      if (!isCurrent(generation, loadGeneration, requestedOrganizationId)) return
      if (currentOffering.organizationId !== requestedOrganizationId) {
        throw new Error(t('rolloutControls.ingestionOffering.organizationMismatch'))
      }
      setOffering(currentOffering)
      if (currentOffering.editable) {
        await refreshHistory(generation, loadGeneration, requestedOrganizationId)
      }
    } catch (loadError) {
      if (!isCurrent(generation, loadGeneration, requestedOrganizationId)) return
      setError(loadError instanceof Error ? loadError.message : t('rolloutControls.loadError'))
    } finally {
      if (isCurrent(generation, loadGeneration, requestedOrganizationId)) {
        setIsLoading(false)
      }
    }
  }, [isCurrent, organization.id, refreshHistory, t])

  const invalidatePendingRequests = useCallback(() => {
    loadGeneration.current++
    mutationGeneration.current++
  }, [])

  useEffect(() => {
    mutationGeneration.current++
    setIsSaving(false)
    void load()
    return invalidatePendingRequests
  }, [invalidatePendingRequests, load])

  const excludedTypes = useMemo(
    () => new Set((offering?.exclusions ?? []).map((exclusion) => exclusion.connectorType)),
    [offering],
  )
  const rows = useMemo<OfferingRow[]>(() => {
    const declared = (offering?.connectors ?? []).map((connector) => ({
      connectorType: connector.connectorType,
      displayName: connector.displayName,
      create: connector.create,
      sync: connector.sync,
      count: connector.activeResources,
      hostCount: connector.hostIds.length,
      stale: false,
    }))
    const declaredTypes = new Set(declared.map((row) => row.connectorType))
    const stale = [...excludedTypes]
      .filter((connectorType) => !declaredTypes.has(connectorType))
      .sort()
      .map((connectorType) => ({ connectorType, displayName: connectorType, create: false, sync: false, count: 0, hostCount: 0, stale: true }))
    return [...declared, ...stale]
  }, [excludedTypes, offering])
  const hosts = offering?.hosts ?? []
  const declaringHosts = hosts.filter((host) => host.status === 'declared')
  const uncoveredResources = useMemo(() => {
    const declaredTypes = new Set((offering?.connectors ?? []).map((connector) => connector.connectorType))
    return (offering?.activeResourceCounts ?? []).filter((row) => !declaredTypes.has(row.connectorType))
  }, [offering])

  const reasonBytes = useMemo(() => new TextEncoder().encode(reason.trim()).length, [reason])
  const readOnly = offering !== null && !offering.editable
  const canEdit = offering !== null && !readOnly
  const canApply = canEdit
    && pending !== null
    && reasonBytes > 0
    && reasonBytes <= reasonMaxBytes
    && confirmed
    && !isSaving

  const isOffered = (connectorType: string): boolean => {
    if (pending?.connectorType === connectorType) return pending.action === 'include'
    return !excludedTypes.has(connectorType)
  }

  const toggle = (connectorType: string, offered: boolean) => {
    const currentlyOffered = !excludedTypes.has(connectorType)
    setPending(offered === currentlyOffered ? null : { connectorType, action: offered ? 'include' : 'exclude' })
    setConfirmed(false)
    setSaved(false)
  }

  const apply = async () => {
    if (!offering || !pending || !canApply) return
    const generation = ++mutationGeneration.current
    const requestedOrganizationId = organization.id
    const change = pending
    setIsSaving(true)
    setError(null)
    setHistoryWarning(null)
    setSaved(false)
    try {
      const command = { revision: offering.revision, reason: reason.trim(), confirmed: true as const }
      const updated: IngestionOfferingState = change.action === 'exclude'
        ? await excludeIngestionConnector(change.connectorType, command)
        : await includeIngestionConnector(change.connectorType, command)
      if (!isCurrent(generation, mutationGeneration, requestedOrganizationId)) return
      if (updated.organizationId !== requestedOrganizationId) {
        throw new Error(t('rolloutControls.ingestionOffering.organizationMismatch'))
      }
      setOffering({ ...offering, revision: updated.revision, exclusions: updated.exclusions ?? [] })
      setPending(null)
      setReason('')
      setConfirmed(false)
      setSaved(true)
      await refreshHistory(generation, mutationGeneration, requestedOrganizationId)
    } catch (saveError) {
      if (!isCurrent(generation, mutationGeneration, requestedOrganizationId)) return
      if (saveError instanceof ApiError && saveError.status === 409) {
        await load()
        if (isCurrent(generation, mutationGeneration, requestedOrganizationId)) {
          setError(t('rolloutControls.ingestionOffering.conflict'))
        }
      } else {
        setError(saveError instanceof Error ? saveError.message : t('rolloutControls.ingestionOffering.saveError'))
      }
    } finally {
      if (isCurrent(generation, mutationGeneration, requestedOrganizationId)) {
        setIsSaving(false)
      }
    }
  }

  const confirmationText = pending
    ? t(pending.action === 'exclude'
      ? 'rolloutControls.ingestionOffering.confirmExclude'
      : 'rolloutControls.ingestionOffering.confirmInclude', { connector: pending.connectorType, organization: organization.name })
    : ''

  return (
    <Card>
      <CardHeader className="border-b border-slate-100 pb-4 dark:border-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-sky-50 p-2 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
              <Database className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <CardTitle>{t('rolloutControls.ingestionOffering.title')}</CardTitle>
              <CardDescription className="mt-1">{t('rolloutControls.ingestionOffering.description')}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="purple">{t('rolloutControls.ingestionOffering.organizationScoped')}</Badge>
            {offering && readOnly && (
              <Badge variant="default">{t('rolloutControls.ingestionOffering.readOnly')}</Badge>
            )}
            {offering && (
              <Badge variant={excludedTypes.size === 0 ? 'success' : 'warning'}>
                {t('rolloutControls.ingestionOffering.excludedCount', { count: excludedTypes.size })}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Spinner /></div>
        ) : error && !offering ? (
          <Alert variant="error">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>{error}</span>
              <Button variant="secondary" size="sm" onClick={() => void load()}>
                <RefreshCw className="mr-1 h-4 w-4" />{t('rolloutControls.refresh')}
              </Button>
            </div>
          </Alert>
        ) : offering ? (
          <>
            {error && <Alert variant="error">{error}</Alert>}
            {saved && <Alert variant="success">{t('rolloutControls.ingestionOffering.saved')}</Alert>}
            {historyWarning && <Alert variant="warning">{historyWarning}</Alert>}
            {readOnly ? (
              <Alert variant="info">
                <span className="flex items-center gap-2"><Eye className="h-4 w-4" aria-hidden="true" />{t('rolloutControls.ingestionOffering.readOnlyNotice')}</span>
              </Alert>
            ) : (
              <Alert variant="info">{t('rolloutControls.ingestionOffering.effectNotice')}</Alert>
            )}

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.7fr)]">
              <div>
                <div className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                  {t('rolloutControls.ingestionOffering.connectorsTitle')}
                </div>
                {rows.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="ingestion-offering-no-connectors">
                    {declaringHosts.length === 0
                      ? t('rolloutControls.ingestionOffering.noDeclaringHost')
                      : t('rolloutControls.ingestionOffering.noConnectors')}
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700" data-testid="ingestion-offering-rows">
                    {rows.map((row) => {
                      const offered = isOffered(row.connectorType)
                      const changed = pending?.connectorType === row.connectorType
                      return (
                        <li
                          key={row.connectorType}
                          data-testid={`ingestion-offering-row-${row.connectorType}`}
                          className={`flex flex-wrap items-center justify-between gap-3 p-3 ${changed ? 'bg-sky-50/70 dark:bg-sky-950/20' : ''}`}
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{row.displayName}</span>
                              <code className="text-xs text-slate-500 dark:text-slate-400">{row.connectorType}</code>
                              <Badge variant={offered ? 'success' : 'warning'} size="sm">
                                {offered ? t('rolloutControls.ingestionOffering.offered') : t('rolloutControls.ingestionOffering.excluded')}
                              </Badge>
                              {row.create && <Badge variant="info" size="sm">{t('rolloutControls.ingestionOffering.capabilityCreate')}</Badge>}
                              {row.sync && <Badge variant="info" size="sm">{t('rolloutControls.ingestionOffering.capabilitySync')}</Badge>}
                            </div>
                            <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-slate-500 dark:text-slate-400">
                              {row.stale ? (
                                <span>{t('rolloutControls.ingestionOffering.staleRow')}</span>
                              ) : (
                                <>
                                  <span>{t('rolloutControls.ingestionOffering.activeResources', { count: row.count })}</span>
                                  <span>{t('rolloutControls.ingestionOffering.declaredByHosts', { count: row.hostCount })}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <Switch
                            id={`ingestion-offering-${row.connectorType}`}
                            checked={offered}
                            disabled={!canEdit || isSaving}
                            aria-label={t('rolloutControls.ingestionOffering.offeredSwitch', { connector: row.connectorType })}
                            onCheckedChange={(checked) => toggle(row.connectorType, checked === true)}
                          />
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.ingestionOffering.currentOrganization')}</dt>
                    <dd className="mt-1 font-medium text-slate-900 dark:text-slate-100">{organization.name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.ingestionOffering.revision')}</dt>
                    <dd className="mt-1 font-mono" data-testid="ingestion-offering-revision">{offering.revision}</dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-1 text-xs uppercase tracking-wide text-slate-500">
                      <Server className="h-3 w-3" aria-hidden="true" />{t('rolloutControls.ingestionOffering.hostsTitle')}
                    </dt>
                    <dd className="mt-1">
                      {hosts.length === 0 ? (
                        <span className="text-slate-500 dark:text-slate-400">{t('rolloutControls.ingestionOffering.noOnlineHosts')}</span>
                      ) : (
                        <ul className="space-y-1" data-testid="ingestion-offering-hosts">
                          {hosts.map((host) => {
                            const reasonKey = host.upstreamReason !== undefined ? hostReasonKeys.get(host.upstreamReason) : undefined
                            return (
                            <li key={host.hostId} className="flex flex-wrap items-center gap-2" data-testid={`ingestion-offering-host-${host.hostId}`}>
                              <span className="font-medium text-slate-800 dark:text-slate-200">{host.name}</span>
                              {host.intelligenceRuntimeVersion && <code className="text-xs text-slate-500">{host.intelligenceRuntimeVersion}</code>}
                              <Badge variant={host.status === 'declared' ? 'success' : 'warning'} size="sm">
                                {host.status === 'declared'
                                  ? t(hostStatusKeyByStatus.declared, { count: host.connectorCount })
                                  : t(hostStatusKeys.get(host.status) ?? hostStatusKeyByStatus.unreadable)}
                              </Badge>
                              {host.status !== 'declared' && host.upstreamStatus !== undefined && (
                                <code className="text-xs text-slate-500">{t('rolloutControls.ingestionOffering.hostUpstreamStatus', { status: host.upstreamStatus })}</code>
                              )}
                              {host.status !== 'declared' && reasonKey !== undefined && (
                                <span className="text-xs text-slate-500 dark:text-slate-400" data-testid={`ingestion-offering-host-reason-${host.hostId}`}>
                                  {t(reasonKey)}
                                </span>
                              )}
                              {host.connectors.length > 0 && (
                                <details className="w-full">
                                  <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200">
                                    {t('rolloutControls.ingestionOffering.hostConnectorsToggle')}
                                  </summary>
                                  <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1" data-testid={`ingestion-offering-host-connectors-${host.hostId}`}>
                                    {host.connectors.map((connector) => (
                                      <li key={connector.connectorType} className="flex items-center gap-1 text-xs">
                                        <span className={connector.offered ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 line-through dark:text-slate-500'}>
                                          {connector.displayName}
                                        </span>
                                        {!connector.offered && (
                                          <Badge variant="warning" size="sm">{t('rolloutControls.ingestionOffering.excluded')}</Badge>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                            </li>
                            )
                          })}
                        </ul>
                      )}
                    </dd>
                  </div>
                  {uncoveredResources.length > 0 && (
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-slate-500">{t('rolloutControls.ingestionOffering.uncoveredTitle')}</dt>
                      <dd className="mt-1 text-slate-600 dark:text-slate-400" data-testid="ingestion-offering-uncovered">
                        {uncoveredResources.map((row) => t('rolloutControls.ingestionOffering.uncoveredItem', { connector: row.connectorType, count: row.count })).join(', ')}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>

            {canEdit && (
              <div>
                <Textarea
                  id="ingestion-offering-reason"
                  label={t('rolloutControls.ingestionOffering.reasonLabel')}
                  placeholder={t('rolloutControls.ingestionOffering.reasonPlaceholder')}
                  value={reason}
                  rows={3}
                  onChange={(event) => {
                    setReason(event.target.value.replace(/[\r\n\t]+/g, ' '))
                    setSaved(false)
                  }}
                  error={reasonBytes > reasonMaxBytes ? t('rolloutControls.ingestionOffering.reasonTooLong') : undefined}
                />
                <div className="mt-1 text-right text-xs text-slate-400">
                  {t('rolloutControls.ingestionOffering.reasonBytes', { bytes: reasonBytes })}
                </div>
              </div>
            )}

            {canEdit && pending && (
              <Checkbox
                id="ingestion-offering-confirmation"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
                label={confirmationText}
              />
            )}

            {/* Catalogue contract: typed API infra_ingestion_offering.service.ts; plan
                PLAN-20260911; removal = delete the catalogue entry. The exclusion
                table, the offering API and the history remain. */}
            <div className="rounded-lg border border-dashed border-slate-300 p-4 dark:border-slate-700">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('rolloutControls.ingestionOffering.retirementTitle')}</div>
              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{t('rolloutControls.ingestionOffering.retirementDescription')}</p>
            </div>

            {!readOnly && (
              <div>
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                  <History className="h-4 w-4" aria-hidden="true" />
                  {t('rolloutControls.ingestionOffering.historyTitle')}
                </div>
                {history.length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">{t('rolloutControls.ingestionOffering.noHistory')}</p>
                ) : (
                  <ol className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
                    {history.map((entry) => (
                      <li key={entry.revision} className="p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium text-slate-800 dark:text-slate-200">
                            {t(entry.action === 'exclude'
                              ? 'rolloutControls.ingestionOffering.historyExclude'
                              : 'rolloutControls.ingestionOffering.historyInclude', { connector: entry.connectorType })}
                          </span>
                          <time className="text-xs text-slate-400" dateTime={entry.changedAt}>
                            {new Intl.DateTimeFormat(i18n.language, historyDateOptions).format(new Date(entry.changedAt))}
                          </time>
                        </div>
                        <p className="mt-1 text-slate-600 dark:text-slate-400">{entry.reason}</p>
                        <div className="mt-1 font-mono text-[11px] text-slate-400">
                          {t('rolloutControls.ingestionOffering.historyEvidence', { actor: entry.actorUserId, revision: entry.revision })}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </>
        ) : null}
      </CardContent>

      {offering && !isLoading && canEdit && (
        <CardFooter className="justify-end border-t border-slate-100 pt-4 dark:border-slate-800">
          <Button variant="primary" disabled={!canApply} isLoading={isSaving} onClick={() => void apply()}>
            {t('rolloutControls.ingestionOffering.apply')}
          </Button>
        </CardFooter>
      )}
    </Card>
  )
}
